import React, { useState, useCallback } from 'react';
import type {
  DiarySettingsFormData,
  DiarySettingsFormLabels,
  DiarySettingsFormProps,
} from '../../shared/types/diarySettingsForm';

export type { DiarySettingsFormData, DiarySettingsFormLabels, DiarySettingsFormProps };

export function DiarySettingsForm({
  initialData,
  onSubmit,
  onCancel,
  errors = {},
  labels: l,
  isSubmitting = false,
}: DiarySettingsFormProps) {
  const [formData, setFormData] = useState<DiarySettingsFormData>(initialData);

  const handleToggle = useCallback((field: keyof DiarySettingsFormData) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  return (
    <form onSubmit={handleSubmit}>
      {/* Error messages */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {Object.entries(errors).flatMap(([field, messages]) =>
              messages.map((msg, i) => (
                <li key={`${field}-${i}`}>{msg}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Widget Visibility Section */}
      <div className="mb-5">
        <div className="d-flex align-items-center mb-3">
          <div className="icon-wrapper icon-wrapper-sm me-3">
            <i className="bi bi-eye"></i>
          </div>
          <h5 className="mb-0 fw-semibold">{l.widgetVisibility}</h5>
        </div>
        <div className="ms-5">
          {/* Show Additional Activities */}
          <div className="form-check p-3 rounded-3 bg-light mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              id="showAdditionalActivities"
              checked={formData.showAdditionalActivities}
              onChange={() => handleToggle('showAdditionalActivities')}
              data-testid="show-additional-activities"
            />
            <label className="form-check-label fw-semibold" htmlFor="showAdditionalActivities">
              {l.showAdditionalActivities}
            </label>
            <div className="form-text mt-1">{l.showAdditionalActivitiesHelp}</div>
          </div>

          {/* Show Food Widget */}
          <div className="form-check p-3 rounded-3 bg-light">
            <input
              type="checkbox"
              className="form-check-input"
              id="showFoodWidget"
              checked={formData.showFoodWidget}
              onChange={() => handleToggle('showFoodWidget')}
              data-testid="show-food-widget"
            />
            <label className="form-check-label fw-semibold" htmlFor="showFoodWidget">
              {l.showFoodWidget}
            </label>
            <div className="form-text mt-1">{l.showFoodWidgetHelp}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="row g-3 mt-4">
        <div className="col-md-6">
          <button
            type="button"
            className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="back-to-diary"
          >
            <i className="bi bi-arrow-left me-2"></i>
            {l.backToDiary}
          </button>
        </div>
        <div className="col-md-6">
          <button
            type="submit"
            className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
            style={{ background: 'var(--traq-gradient-primary)', border: 'none' }}
            disabled={isSubmitting}
            data-testid="save-settings"
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {l.save}
              </>
            ) : (
              l.save
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

export default DiarySettingsForm;
